/**
 * ApiClient (apiFetch)
 * --------------------
 * Centralized API request wrapper.
 *
 * - Attaches Authorization: Bearer <token> using Session.getToken().
 * - Normalizes responses to { ok, status, data }.
 * - Handles 401 centrally by clearing Session (logged-out state).
 *
 * Callers must NOT manually attach Authorization headers.
 */

import Session from "../auth/Session";

function tryParseJson(text) {
    if (typeof text !== "string") return null;
    const trimmed = text.trim();
    if (!trimmed) return null;

    // quick guard: most JSON starts with { or [
    const c = trimmed[0];
    if (c !== "{" && c !== "[") return null;

    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
}

function defaultErrorPayload(status) {
    // Keep compatible with your backend contract without guessing “ApiErrorDto”
    // (but structured enough for UI to show something meaningful).
    return {
        code: "http_error",
        message: `Request failed (${status}).`
    };
}

export default async function apiFetch(url, options = {}) {
    const token = Session.getToken();

    const headers = new Headers(options.headers || {});
    if (token) headers.set("Authorization", `Bearer ${token}`);

    // If caller passes a plain object body, they should stringify it themselves.
    const response = await fetch(url, { ...options, headers });

    // 204: no content
    if (response.status === 204) {
        return { ok: true, status: 204, data: null };
    }

    let data = null;
    const ct = (response.headers.get("content-type") || "").toLowerCase();

    try {
        if (ct.includes("application/json")) {
            data = await response.json();
        } else {
            // Read text and attempt to parse JSON anyway (some servers mislabel content-type)
            const text = await response.text();
            const maybeJson = tryParseJson(text);
            data = maybeJson ?? text;
        }
    } catch {
        data = null;
    }

    // Central auth handling
    if (response.status === 401) {
        Session.removeUserSession();
        // Ensure callers get a predictable shape for UI messaging if desired.
        if (data == null || data === "") data = { code: "unauthorized", message: "Please log in again." };
    }

    // For non-OK responses, guarantee *some* structured payload (prevents UI from exploding on null/empty)
    if (!response.ok) {
        if (data == null || data === "") data = defaultErrorPayload(response.status);
        // If it's plain text, wrap it so UI can use .message
        if (typeof data === "string") data = { code: "error", message: data };
    }

    return {
        ok: response.ok,
        status: response.status,
        data
    };
}
